<template lang="pug">
    v-container.disabled-alternate(data-cy="template-editor")
      v-snackbar(v-model="showAttachmentSizeError" :auto-height="true" color="error")
        div.attachment-size-error {{ attachmentSizeErrorText }}
      v-text-field(
        data-cy="template-description-field"
        label="Description"
        v-model="template.description"
        @input="onInput"
        :disabled="!isEditing"
        prepend-icon="notes"
        :error-messages="descriptionError ? [descriptionError] : []"
      )

      v-select(
        data-cy="template-folder-select"
        :items="templateFolders"
        v-model="template.template_folder_id"
        :disabled="!isEditing"
        @input="onInput"
        label="Template Folder"
        prepend-icon="folder"
      )

      //- Choose template Type
      v-select(
        data-cy="template-type-select"
        v-if="canChooseType || isCreatingTemplate"
        :items="communicationTypes"
        v-model="template.template_type"
        @input="onInput"
        label="Template Type"
        prepend-icon="devices_other"
      )

      liondesk-content-editor(
        data-cy="template-subject-content-editor"
        v-show="template.template_type === 'Email'"
        v-model="template.subject"
        @input="onInput"
        :disabled="!isEditing"
        label="Subject"
        field-type="input"
        ref-name="templateSubjectField"
        icon="email"
        activator-size="shrink"
        input-size="grow"
        hideActivator
        isEmailSubject
      )
        template(slot="activator-bottom-insert-content")
          div.text-xs-right(v-if="isEditing")
            v-btn.title-case(flat small color="primary")
              span.title-case Insert LionDesk Content

      liondesk-content-editor.mt-4(
        v-show="template.template_type === 'Email'"
        :attachment-handler-id="attachmentHandlerId"
        v-model="template.body"
        @input="updateTemplateBody"
        ref-name="action-preview-editor"
        field-type="quill"
        :disabled="!isEditing"
      )

      rich-text-editor(
        data-cy="template-editor-rich-text-editor"
        v-if="template.template_type === 'Text'"
        toolType="sms"
        communicationType="text"
        v-model="template.body"
        :disabled="!isEditing"
        @input="updateTemplateBody"
      )

      v-text-field(
        v-if="template.template_type !== 'Text' && template.template_type !== 'Email'"
        label="This template type is not supported yet"
        disabled
      )

      attachment-handler(
        :input-id="attachmentHandlerId"
        :files="templateFiles"
        :uploadedFiles="template.files"
        :is-disabled="!isEditing"
        @change="handleUpload"
        @error="handleFileError"
      )

      // TODO cc - refactor
      liondesk-dialog(
        :value="showFileError"
        dialogTitle="Failed to attach file"
        cancelButtonText="Close"
        okButtonText="Try Another File"
        @click:cancel="closeErrors"
        @click:ok="tryUploadAgain"
      )
        div.error-text(slot="body")
          span The following file{{ fileErrors.length > 1 ? 's' : '' }} could not be attached:
          ul.files
            li(v-for="(file, index) in fileErrors" :key="index") {{ file.name }}
          span Please ensure the file size does not exceed 15 MB.
</template>
<script>
import fileSize from '@/Core/filters/fileSize';
import FileUpload from '@/Core/mixins/file-upload';
import LiondeskContentEditor from '@/Communications/components/LiondeskContentEditor';
import RichTextEditor from '@/Core/components/richTextareaEditor/RichTextEditor';
import EmojiPicker from '@/Core/components/richTextareaEditor/EmojiPicker';
import TitleBar from '@/Core/components/TitleBar';
import AttachmentHandler from '@/Communications/components/AttachmentHandler';
import LiondeskDialog from '@/Core/components/LiondeskDialog';
import { MAX_ATTACHMENT_SIZE_BYTES, MAX_ATTACHMENT_SIZE_MEGABYTES, RECOMMENDED_ATTACHMENT_SIZE_MEGABYTES } from '@/Email/config/email-limits';
import extractTextFromHtml from '@/Text/utilities/extractTextFromHtml';

export default {
  components: {
    LiondeskContentEditor,
    RichTextEditor,
    EmojiPicker,
    TitleBar,
    AttachmentHandler,
    LiondeskDialog,
  },
  mixins: [FileUpload],
  data() {
    return {
      // Controls the state whether the components are writable or not
      fieldsTitle: '{{fields}}',
      attachmentHandlerId: 'template-attachment',

      templateFiles: [],
      template: {},
      communicationTypes: ['Email', 'Text'],
      showedAttachmentSizeError: false,
      attachmentSizeErrorText:
        `The attachments on this message total more than the limit of ${MAX_ATTACHMENT_SIZE_MEGABYTES}MB. For best deliverability, we recommend that email attachments total less than ${RECOMMENDED_ATTACHMENT_SIZE_MEGABYTES}MB or that you put links to the files in the body of your email.`,
    };
  },
  props: {
    /**
     * indicate whether this should be a new template form or a template update form
     * Should have either the value 'create' or 'update'
     */
    formAction: {
      type: String,
      required: true,
    },
    templateFolders: {
      type: Array,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
    canChooseType: {
      type: Boolean,
      default: false,
    },
    /**
     * Boolean to control whether the form updates or creates a template
     */
    isCreatingTemplate: {
      type: Boolean,
      default: false,
    },
    /**
     * The template object
     */
    value: Object,
    descriptionError: null,
  },
  computed: {
    /**
     * Determine if save btn is disabled or not
     * If any of the following fields is changed, return false: 'body', 'description', 'subject'
     */
    isDisabled() {
      const { template } = this;
      const noFolder = !template.template_folder_id;
      const emptyBody = !template.body || (template.body && !template.body.trim());
      const emptyDescription = !template.description || (template.description && !template.description.trim());
      const emptySubject = template.template_type === 'Email' && (!template.subject || (template.subject && !template.subject.trim()));

      return emptyBody || emptySubject || emptyDescription || noFolder;
    },
    invalidAttachmentSize() {
      if (this.template.template_type !== 'Email') {
        return false;
      }
      return this.templateFiles.reduce((totalSize, file) => totalSize + file.size_bytes, 0)  > MAX_ATTACHMENT_SIZE_BYTES;
    },
    showAttachmentSizeError: {
      get() {
        return this.invalidAttachmentSize && !this.showedAttachmentSizeError;
      },
      set() {
        this.showedAttachmentSizeError = true;
      },
    }
  },
  filters: {
    fileSize,
  },
  watch: {
    value(newValue) {
      if (newValue.template_type && newValue.template_type === 'Text') {
        const textBody = newValue.subject || newValue.body;
        this.template = Object.assign({}, newValue, { body: textBody });
      } else {
        this.template = Object.assign({}, newValue);
      }
      this.templateFiles = [];
      this.showedAttachmentSizeError = false;
    }
  },
  methods: {
    handleUpload(value) {
      this.templateFiles = [...value];
      this.template = Object.assign(this.template, { files: this.templateFiles });
      this.onInput();
    },
    updateTemplateBody(newValue) {
      // const newBody = this.template.template_type === 'Text' ? extractTextFromHtml(newValue) : newValue;
      const templateCopy = Object.assign({}, this.template, { body: newValue });
      this.template = templateCopy;
      this.onInput();
    },
    onInput() {
      this.$emit('input', this.template);
      this.$emit('content-valid', !this.isDisabled);
    },
    save() {
      if (this.invalidAttachmentSize) {
        this.showedAttachmentSizeError = false;
        return
      }
      this.$emit(`${this.formAction}-template`, this.template);
    },
    close(confirm) {
      this.$emit('cancel', confirm);
    },
  },
};
</script>
